// src/components/personal/AreaPositionSelector.jsx
//
// Selector en dos pasos para el rol principal de un empleado: primero el
// Área (Pista o Mini), después la(s) Posición(es) dentro de esa área. Pista
// solo tiene una -> queda fija; Mini tiene Caja/Reponedor -> default Caja,
// con la posibilidad de agregar Reponedor como chip si la persona hace
// ambas cosas dentro de la misma área. Reemplaza el combo único "Área – Rol"
// que mezclaba ambos niveles en una sola lista plana.
//
// `options` es el mismo array combinado que ya se arma en EditPersonalForm.js
// / AddPersonalModal.js: [{ id, positionId, positionName, roleName, label }],
// uno por cada `workarea` real.
//
// Modo `multiple` (usado para el rol principal): `value` es un ARRAY de esas
// opciones, todas de la misma área -- la primera del array es la que se
// guarda como id_workarea principal; el resto se agrega como auxiliar
// "dentro de la misma área" (el llamador decide cómo mandarlo al backend).
// Modo simple (`multiple=false`): `value` es un único objeto o null.

import React, { useMemo } from 'react';
import { TextField, MenuItem, Box, Autocomplete } from '@mui/material';

const AreaPositionSelector = ({
  options = [],
  value,
  onChange,
  multiple = false,
  disabled = false,
  areaLabel = 'Área',
  positionLabel = 'Posición',
  error,
  helperText,
  excludeAreaIds = [], // ej. el área ya usada como principal, para "Roles auxiliares"
}) => {
  const excludedSet = useMemo(() => new Set(excludeAreaIds || []), [excludeAreaIds]);

  const areas = useMemo(() => {
    const seen = new Map();
    options.forEach((opt) => {
      if (opt.positionId != null && !excludedSet.has(opt.positionId) && !seen.has(opt.positionId)) {
        seen.set(opt.positionId, { id: opt.positionId, name: opt.positionName });
      }
    });
    return Array.from(seen.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [options, excludedSet]);

  const selectedAreaId = multiple
    ? (value?.[0]?.positionId ?? '')
    : (value?.positionId ?? '');

  const positionsForArea = useMemo(
    () => options
      .filter((opt) => opt.positionId === selectedAreaId)
      .sort((a, b) => String(a.roleName).localeCompare(String(b.roleName))),
    [options, selectedAreaId]
  );

  const handleAreaChange = (e) => {
    const newAreaId = e.target.value;
    // Al elegir el área, se pre-selecciona la primera posición disponible
    // (única en el caso de Pista; la primera alfabéticamente en Mini, ej. Caja).
    const firstOption = options.find((opt) => opt.positionId === newAreaId) || null;
    onChange(multiple ? (firstOption ? [firstOption] : []) : firstOption);
  };

  const handlePositionChange = (e) => {
    const newOption = options.find((opt) => String(opt.id) === String(e.target.value)) || null;
    onChange(newOption);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
      <TextField
        select
        fullWidth
        label={areaLabel}
        value={selectedAreaId}
        onChange={handleAreaChange}
        disabled={disabled || !areas.length}
      >
        <MenuItem value="">
          <em>Seleccionar…</em>
        </MenuItem>
        {areas.map((a) => (
          <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
        ))}
      </TextField>

      {multiple ? (
        <Autocomplete
          multiple
          disableClearable={false}
          options={positionsForArea}
          getOptionLabel={(opt) => opt?.roleName ?? ''}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          value={value || []}
          onChange={(_, newValue) => onChange(newValue)}
          disabled={disabled || !selectedAreaId}
          renderInput={(params) => (
            <TextField
              {...params}
              label={positionLabel}
              error={error}
              helperText={helperText}
            />
          )}
        />
      ) : (
        <TextField
          select
          fullWidth
          label={positionLabel}
          value={value?.id ?? ''}
          onChange={handlePositionChange}
          disabled={disabled || positionsForArea.length <= 1}
          error={error}
          helperText={helperText}
        >
          <MenuItem value="">
            <em>Seleccionar…</em>
          </MenuItem>
          {positionsForArea.map((opt) => (
            <MenuItem key={opt.id} value={opt.id}>{opt.roleName}</MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
};

export default AreaPositionSelector;
